import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type CSSProperties, type ReactNode } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { AlertTriangle, CheckCircle2, Info, X, XCircle } from "lucide-react";

type SileoTone = "success" | "error" | "info" | "warning";

type SileoItem = {
  id: number;
  tone: SileoTone;
  message: string;
};

type SileoShowOptions = {
  durationMs?: number;
};

type SileoContextValue = {
  show: (tone: SileoTone, message: string, options?: SileoShowOptions) => void;
  success: (message: string, options?: SileoShowOptions) => void;
  error: (message: string, options?: SileoShowOptions) => void;
  info: (message: string, options?: SileoShowOptions) => void;
  warning: (message: string, options?: SileoShowOptions) => void;
};

const DEFAULT_DURATION_MS = 3000;
const SileoContext = createContext<SileoContextValue | null>(null);

const baseToastStyle: CSSProperties = {
  pointerEvents: "auto",
  borderRadius: 16,
  borderWidth: 1,
  borderStyle: "solid",
  padding: "12px 12px",
  display: "flex",
  gap: 10,
  alignItems: "flex-start",
  boxShadow: "0 18px 36px rgba(15,23,42,.2)",
  backdropFilter: "blur(8px)",
  background: "rgba(15,23,42,.92)",
};

function toneStyles(tone: SileoTone) {
  switch (tone) {
    case "success":
      return {
        borderColor: "rgba(16,185,129,.42)",
        iconBg: "rgba(16,185,129,.2)",
        iconColor: "#86efac",
        textColor: "#ecfdf5",
        Icon: CheckCircle2,
      };
    case "error":
      return {
        borderColor: "rgba(244,63,94,.42)",
        iconBg: "rgba(244,63,94,.2)",
        iconColor: "#fecdd3",
        textColor: "#fff1f2",
        Icon: XCircle,
      };
    case "warning":
      return {
        borderColor: "rgba(245,158,11,.42)",
        iconBg: "rgba(245,158,11,.2)",
        iconColor: "#fde68a",
        textColor: "#fffbeb",
        Icon: AlertTriangle,
      };
    default:
      return {
        borderColor: "rgba(59,130,246,.42)",
        iconBg: "rgba(59,130,246,.2)",
        iconColor: "#bfdbfe",
        textColor: "#eff6ff",
        Icon: Info,
      };
  }
}

export function SileoProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<SileoItem[]>([]);
  const ids = useRef(0);
  const timersRef = useRef<Record<number, number>>({});

  const clearTimer = useCallback((id: number) => {
    const timer = timersRef.current[id];
    if (timer) {
      window.clearTimeout(timer);
      delete timersRef.current[id];
    }
  }, []);

  const dismiss = useCallback(
    (id: number) => {
      clearTimer(id);
      setItems((current) => current.filter((item) => item.id !== id));
    },
    [clearTimer]
  );

  const show = useCallback(
    (tone: SileoTone, message: string, options?: SileoShowOptions) => {
      const trimmed = String(message ?? "").trim();
      if (!trimmed) {
        return;
      }
      const id = ++ids.current;
      const durationMs = Math.max(1200, Number(options?.durationMs || DEFAULT_DURATION_MS));
      setItems((current) => [...current, { id, tone, message: trimmed }]);
      timersRef.current[id] = window.setTimeout(() => dismiss(id), durationMs);
    },
    [dismiss]
  );

  useEffect(() => {
    return () => {
      const allTimers = Object.values(timersRef.current);
      allTimers.forEach((timer) => window.clearTimeout(timer));
      timersRef.current = {};
    };
  }, []);

  const value = useMemo<SileoContextValue>(
    () => ({
      show,
      success: (message, options) => show("success", message, options),
      error: (message, options) => show("error", message, options),
      info: (message, options) => show("info", message, options),
      warning: (message, options) => show("warning", message, options),
    }),
    [show]
  );

  return (
    <SileoContext.Provider value={value}>
      {children}
      <div
        style={{
          position: "fixed",
          top: 14,
          left: "50%",
          transform: "translateX(-50%)",
          width: "min(92vw, 560px)",
          display: "flex",
          flexDirection: "column",
          gap: 10,
          zIndex: 2600,
          pointerEvents: "none",
        }}
      >
        <AnimatePresence initial={false}>
          {items.map((item) => {
            const tone = toneStyles(item.tone);
            const Icon = tone.Icon;
            return (
              <motion.div
                key={item.id}
                initial={{ opacity: 0, y: -10, scale: 0.97 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: -8, scale: 0.97 }}
                transition={{ duration: 0.16 }}
                style={{ ...baseToastStyle, borderColor: tone.borderColor }}
                role="status"
                aria-live="polite"
              >
                <div
                  style={{
                    width: 30,
                    height: 30,
                    borderRadius: 10,
                    flexShrink: 0,
                    display: "grid",
                    placeItems: "center",
                    background: tone.iconBg,
                    color: tone.iconColor,
                  }}
                >
                  <Icon size={16} />
                </div>
                <div style={{ color: tone.textColor, fontSize: 14, lineHeight: 1.35, fontWeight: 600, flex: 1 }}>
                  {item.message}
                </div>
                <button
                  type="button"
                  onClick={() => dismiss(item.id)}
                  aria-label="Cerrar notificacion"
                  style={{
                    border: "none",
                    background: "transparent",
                    color: "rgba(255,255,255,.72)",
                    padding: 4,
                    borderRadius: 8,
                    cursor: "pointer",
                  }}
                >
                  <X size={16} />
                </button>
              </motion.div>
            );
          })}
        </AnimatePresence>
      </div>
    </SileoContext.Provider>
  );
}

export function useSileo() {
  const context = useContext(SileoContext);
  if (!context) {
    throw new Error("useSileo must be used within SileoProvider");
  }
  return context;
}
